import { ref, set, push, remove, onValue } from 'firebase/database';
import { useEffect, useState } from 'react';
import { db } from './config';

// Master emails Group Lobu (acceso admin sin restricción de tenant)
export const MASTER_EMAILS = [
  'jllozanorodrigo@gmail.com',
  'grouplobuapp@gmail.com',
  'interlobuservicios@gmail.com',
];

// Legacy export — apunta al primer master para compatibilidad con código existente
export const ADMIN_EMAIL = MASTER_EMAILS[0];

/** True si el email es un master Group Lobu */
export function isMasterEmail(email?: string | null): boolean {
  if (!email) return false;
  return MASTER_EMAILS.includes(email.toLowerCase());
}

/** Comprueba si el user es admin: master O claim tenantRole==='admin' */
export async function isAdminUser(user: { email?: string | null; getIdTokenResult?: () => Promise<{ claims: Record<string, unknown> }> } | null | undefined): Promise<boolean> {
  if (!user) return false;
  if (isMasterEmail(user.email)) return true;
  try {
    const tok = await user.getIdTokenResult?.();
    const claims = (tok?.claims || {}) as Record<string, unknown>;
    return claims.tenantRole === 'admin';
  } catch {
    return false;
  }
}

export type CenterConfig = {
  name?: string;
  address?: string;
  phone?: string;
  createdAt?: number;
};

export type DeviceAssignment = {
  imei: string;
  deviceName?: string;
  deviceModel?: string;
  simNumber?: string;
};

export type UnassignedDevice = {
  imei: string;
  deviceName?: string;
  lastSeen?: number;
  lastLat?: number;
  lastLng?: number;
  status?: string;
};

// ---------- Centers ----------

export async function saveCenterConfig(centerId: string, cfg: CenterConfig) {
  const id = centerId.trim();
  if (!id) throw new Error('centerId vacío');
  await set(ref(db, `centers/${id}/config`), {
    name: cfg.name?.trim() || '',
    address: cfg.address?.trim() || '',
    phone: cfg.phone?.trim() || '',
    createdAt: cfg.createdAt || Date.now(),
  });
}

export async function deleteCenter(centerId: string) {
  await remove(ref(db, `centers/${centerId}`));
}

// ---------- Residents ----------

export async function saveResident(centerId: string, uid: string, cfg: {
  name?: string; resp?: string; photo?: string;
}) {
  await set(ref(db, `centers/${centerId}/tracked/${uid}/config`), {
    name: cfg.name?.trim() || '',
    resp: cfg.resp?.trim() || '',
    photo: cfg.photo?.trim() || '',
    created: new Date().toISOString(),
  });
}

export async function deleteResident(centerId: string, uid: string) {
  await remove(ref(db, `centers/${centerId}/tracked/${uid}`));
}

// ---------- Device assignments ----------

export async function assignDevice(centerId: string, uid: string, device: DeviceAssignment) {
  if (!device.imei.trim()) throw new Error('IMEI vacío');
  await set(ref(db, `device_assignments/${centerId}/${uid}`), {
    imei: device.imei.trim(),
    deviceName: device.deviceName?.trim() || '',
    deviceModel: device.deviceModel?.trim() || '',
    simNumber: device.simNumber?.trim() || '',
  });
  // Limpiar el unassigned correspondiente
  await remove(ref(db, `unassigned_devices/${device.imei.trim()}`));
}

export async function unassignDevice(centerId: string, uid: string) {
  await remove(ref(db, `device_assignments/${centerId}/${uid}`));
}

export async function deleteUnassignedDevice(imei: string) {
  await remove(ref(db, `unassigned_devices/${imei}`));
}

// ---------- Hooks ----------

export function useCenters() {
  const [centers, setCenters] = useState<{ id: string; config?: CenterConfig; trackedCount: number }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onValue(
      ref(db, 'centers'),
      (snap) => {
        const v = snap.val() as Record<string, { config?: CenterConfig; tracked?: Record<string, unknown> }> | null;
        const list = v
          ? Object.entries(v).map(([id, data]) => ({
              id,
              config: data?.config,
              trackedCount: data?.tracked ? Object.keys(data.tracked).length : 0,
            }))
          : [];
        setCenters(list);
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, []);

  return { centers, loading };
}

export function useUnassignedDevices() {
  const [devices, setDevices] = useState<UnassignedDevice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onValue(
      ref(db, 'unassigned_devices'),
      (snap) => {
        const v = snap.val() as Record<string, UnassignedDevice> | null;
        const list = v ? Object.values(v).filter(Boolean) : [];
        setDevices(list);
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, []);

  return { devices, loading };
}

export function useDeviceAssignments(centerId: string | null) {
  const [byUid, setByUid] = useState<Record<string, DeviceAssignment>>({});
  useEffect(() => {
    if (!centerId) { setByUid({}); return; }
    return onValue(
      ref(db, `device_assignments/${centerId}`),
      (snap) => setByUid((snap.val() as Record<string, DeviceAssignment>) || {}),
    );
  }, [centerId]);
  return byUid;
}

export function generateResidentUid(): string {
  return `r_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
}

export { push as rtdbPush };
